import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAddAppointmentNote } from '@/scheduling/services/use-add-appointment-note';

/**
 * Internal-note dialog. Mirrors Flutter `showAddNoteAlert`
 * (calendar_tab.dart:1471): textarea seeded with the current note,
 * Save fires PUT /api/Appointment/{id}/Note. 300-char limit per Flutter.
 */

interface NoteTarget {
  appointmentId: string;
  /** Existing comment, if any — pre-fills the textarea. */
  initialNote?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: NoteTarget | null;
}

const MAX_LEN = 300;

export function AddNoteDialog({ open, onOpenChange, target }: Props) {
  const [note, setNote] = useState('');
  const mutation = useAddAppointmentNote();

  useEffect(() => {
    if (open) setNote(target?.initialNote ?? '');
  }, [open, target?.initialNote]);

  if (!target) return null;

  const submit = async () => {
    await mutation.mutateAsync({ id: target.appointmentId, note });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <DialogHeader>
          <DialogTitle>Add internal note</DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, MAX_LEN))}
            autoFocus
            rows={5}
            placeholder="Add internal note"
            className="w-full px-3 py-2 text-sm rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
          />
          <div className="flex justify-end mt-1 text-xs text-text-hint tabular-nums">
            {note.length} / {MAX_LEN}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
